import { useContext, useRef, useState } from 'react';
import { transcribeAudio } from '../api/transcribe';
import { AuthContext } from '../context/AuthContext';

/**
 * Hook pour l'enregistrement vocal et la transcription.
 *
 * @param {function} onTranscript - Callback appelé avec le texte transcrit.
 * @returns {{
 *   recording: boolean,
 *   transcribing: boolean,
 *   stream: MediaStream|null,
 *   startRecording: function,
 *   confirmRecording: function,
 *   cancelRecording: function,
 *   toggleRecording: function,
 * }}
 */
export function useVoiceRecorder(onTranscript) {
  const { user } = useContext(AuthContext);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [stream, setStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const autoStopTimer = useRef(null);

  const clearAutoStop = () => {
    if (autoStopTimer.current) {
      clearTimeout(autoStopTimer.current);
      autoStopTimer.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      setStream(s);
      const recorder = new MediaRecorder(s);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setRecording(true);

      autoStopTimer.current = setTimeout(() => confirmRecording(), 60 * 1000);
    } catch (err) {
      console.error('Erreur micro:', err);
      alert("Impossible d'accéder au micro.");
    }
  };

  const confirmRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.onstop = async () => {
      stream?.getTracks().forEach((t) => t.stop());
      setStream(null);
      setRecording(false);
      clearAutoStop();

      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      setTranscribing(true);
      const transcript = await transcribeAudio(audioBlob, user.authToken);
      setTranscribing(false);

      if (transcript) onTranscript(transcript);
    };

    recorder.stop();
  };

  const cancelRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.onstop = () => {
      stream?.getTracks().forEach((t) => t.stop());
      setStream(null);
    };

    recorder.stop();
    setRecording(false);
    clearAutoStop();
  };

  const toggleRecording = () => {
    if (recording) confirmRecording();
    else startRecording();
  };

  return {
    recording,
    transcribing,
    stream,
    startRecording,
    confirmRecording,
    cancelRecording,
    toggleRecording,
  };
}
